import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import {
  NOS,
  PERSONAL_INFO,
  PROGRAMME,
  PANEL_AND_SCHEME,
  SUPERVISOR_INFO,
} from '../../../../DRCAssessment';
import type { ApplicantParticulars } from '../../../../graphql';
import { PAGE_TEXT } from '../../../../lang';
import { AssessmentGrid, type RowData } from '../../../../components';
import { Button } from '../../../../../../components';
import { getOptionsLabel } from '../../../../../Monitor/components';
import { panelOptions } from '../../../../../Nomination/components';

type ApplicantParticularsBoxProps = {
  applicantParticulars: ApplicantParticulars;
  showGoDetail: boolean;
};

const { DRC_ASSESSMENT_DETAILS } = PAGE_TEXT;
const PARTICULARS_TEXT = DRC_ASSESSMENT_DETAILS.PARTICULARS;
export const ApplicantParticularsBox: React.FunctionComponent<
  ApplicantParticularsBoxProps
> = props => {
  const { applicationId } = useParams();
  const { applicantParticulars, showGoDetail } = props;

  const { nos, personalInfo, programmeInfo, nominationInfo } =
    applicantParticulars;
  const { panel, fundingSchemes, chiefSupervisorInfo } = nominationInfo;

  const NosHKPFSInfoRowData: RowData[] = [
    {
      label: NOS.HKPFS_REF_NO,
      value: nos.HKPFSRefNo,
    },
    {
      label: PARTICULARS_TEXT.VALIDE_HKPFS,
      // Heavy check mark for valid; Heavy multiplication for invalid; '-' for no verification
      value:
        nos.HKPFSRefNoValidation === null
          ? '-'
          : nos.HKPFSRefNoValidation
            ? '\u2714'
            : '\u2716',
    },
  ];

  const NosRowData: RowData[] = [
    { label: NOS.APPLICATION_NO, value: nos.applicationNo },
    ...NosHKPFSInfoRowData,
  ];

  const personalInfoRowData: RowData[] = [
    {
      label: PERSONAL_INFO.SURNAME,
      value: personalInfo.surName,
    },
    {
      label: PERSONAL_INFO.FIRSTNAME,
      value: personalInfo.firstName,
    },
    {
      label: PERSONAL_INFO.CHINESE_NAME,
      value: personalInfo.chineseName,
    },
    {
      label: PERSONAL_INFO.NATIONALITY,
      value: personalInfo.nationality,
    },
    {
      label: PERSONAL_INFO.IDENTITY_DOCUMENT_TYPE,
      value: personalInfo.identityDocumentType,
    },
    {
      label: PERSONAL_INFO.IDENTITY_DOCUMENT_NUMBER,
      value: personalInfo.identityDocumentNumber,
    },
    {
      label: PERSONAL_INFO.TELEPHONE_NUMBER,
      value: personalInfo.telephoneNumber,
    },
    {
      label: PERSONAL_INFO.ADDRESS,
      value: personalInfo.address,
    },
    {
      label: PERSONAL_INFO.LOCAL_INDICATOR,
      value: personalInfo.localIndicator,
    },
    {
      label: PERSONAL_INFO.RESIDENCE_TYPE,
      value: personalInfo.residenceType,
    },
    {
      label: PERSONAL_INFO.GENDER,
      value: personalInfo.gender,
    },
    {
      label: PERSONAL_INFO.BIRTH,
      value: personalInfo.birth,
    },
    {
      label: PERSONAL_INFO.EMAIL,
      value: personalInfo.email,
    },
    {
      label: PERSONAL_INFO.MOBILE_NUMBER,
      value: personalInfo.mobileNumber,
    },
    {
      label: PERSONAL_INFO.DISABILITY,
      value: personalInfo.disability,
    },
    {
      label: PERSONAL_INFO.DISABILITY_REMARK,
      value: personalInfo.disabilityRemark,
    },
    {
      label: PERSONAL_INFO.UPDATED_AT,
      value: personalInfo.updatedAt,
    },
    {
      label: PERSONAL_INFO.CREATED_AT,
      value: personalInfo.createdAt,
    },
  ];

  const programmeData: RowData[] = [
    {
      label: PROGRAMME.DEPARTMENT,
      value: programmeInfo.department,
    },
    {
      label: PROGRAMME.STUDY_MODE,
      value: programmeInfo.studyMode,
    },
    {
      label: PROGRAMME.RESEARCH_AREA,
      value: programmeInfo.researchArea,
    },
    {
      label: PROGRAMME.ENTRY_YEAR,
      value: programmeInfo.entryYear,
    },
  ];

  const chiefSupervisorInfoRowData: RowData[] = [
    {
      label: SUPERVISOR_INFO.PROPOSED_CHIEF_SUPERVISOR,
      value: chiefSupervisorInfo.proposedChiefSupervisor,
    },
    {
      label: SUPERVISOR_INFO.CHIEFSUPERVISOR_MAIL,
      value: chiefSupervisorInfo.chiefSupervisorMail,
    },
  ];

  const panelAndScheme: RowData[] = [
    {
      label: PANEL_AND_SCHEME.PANEL,
      value: getOptionsLabel(panelOptions, panel ?? ''),
      boldLabel: true,
    },
    {
      label: PANEL_AND_SCHEME.SCHEMES,
      value: fundingSchemes.join(', '),
      boldLabel: true,
    },
  ];

  const handleGoDetails = (): void => {
    window.open(`/internal/monitor/${applicationId}`);
  };

  return (
    <ContentBox>
      <Box className="w-full" padding={2}>
        {[
          { rows: NosRowData },
          { rows: personalInfoRowData },
          {
            rows: programmeData,
            header: PARTICULARS_TEXT.PROGRAMME,
          },
          { rows: panelAndScheme },
          { rows: chiefSupervisorInfoRowData },
        ].map((item, index, array) => (
          <React.Fragment key={index}>
            {item.header !== null && item.header !== undefined && (
              <h3 className="font-bold mb-4">{String(item.header)}</h3>
            )}

            <AssessmentGrid rows={item.rows} />
            {index !== array.length - 1 && <hr />}
          </React.Fragment>
        ))}
      </Box>
      {showGoDetail && (
        <Box className="w-full" padding={2} margin={12}>
          {PARTICULARS_TEXT.MORE}

          <Button variant="outlined" onClick={handleGoDetails}>
            {PARTICULARS_TEXT.GO_DETAILS}
          </Button>
        </Box>
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  margin: 0.5rem 1rem 0;
  font-size: 14px;

  hr {
    margin-top: 1rem;
    margin-bottom: 1rem;
    background-image: linear-gradient(to right, #fff, #ccc, #fff);
  }

  button {
    margin-left: 4rem;
    border-radius: 16px;
    cursor: pointer;
    box-shadow: 1px 1px 1px 1px rgba(0, 0, 0, 0.25);
  }
`;
